import React, { useState, useEffect } from "react";
import { Form } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import SuccessMessage from "../customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import RediMissingContactBanner from "./RediMissingContactBanner";
import {
  Container,
  BackButton,
  StyledBackIcon,
  BackText,
  ContentSection,
  Title,
  Subtitle,
  OptionsContainer,
  OptionWrapper,
  OptionContent,
  OptionLabel,
  ContactInfo,
  ContactText,
  AddContactLink,
  EditIcon,
  ActionsSection,
  ButtonsGroup,
  StyledCheckbox,
  ErrorMessage as StyledErrorMessage,
  RediSecondaryButton,
  RediPrimaryButton
} from "./styles";
import { MPD_CONTACT_SECTION_HASH, RETRIGGER_REDI_AFTER_EDIT_PARAM } from "../../constant/redi";
import { formatPhoneNumber } from "../../utils/formaters";

const CommunicationSettings = ({ onClose, onSave, contactInfo }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { patientId, journeyId } = useParams();
  const [form] = Form.useForm();
  const [patientEmail, setPatientEmail] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (contactInfo) {
      const email = contactInfo.email || "";
      const phone = contactInfo.phone || "";

      setPatientEmail(email);
      setPatientPhone(phone);

      const hasEmailData = contactInfo.hasEmail || false;
      const hasPhoneData = contactInfo.hasSMS || false;

      form.setFieldsValue({
        smsEnabled: hasPhoneData,
        emailEnabled: hasEmailData
      });
    }
  }, [contactInfo, form]);

  const handleNavigateToContactDetails = () => {
    // Use RETRIGGER_REDI_AFTER_EDIT_PARAM so PatientSingleApplication knows to call
    // check-and-prepare with CONTACT_EDIT_RETURN action, which bypasses the 24h cooldown
    // and max-display count since the user is mid-flow completing their contact info.
    const returnPath = `${history.location.pathname}?${RETRIGGER_REDI_AFTER_EDIT_PARAM}=true`;
    history.push(`/patient/${patientId}/journey/${journeyId}/edit${MPD_CONTACT_SECTION_HASH}`, {
      path: returnPath,
      scrollToSection: "manage-patient-contact-details"
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    const { smsEnabled, emailEnabled } = allValues;
    const isValid = smsEnabled || emailEnabled;

    if (!isValid && !hasMissingContactInfo) {
      setFormError(t("redi.communication_settings.at_least_one_required"));
    } else {
      setFormError(null);
    }
  };

  const handleFinish = async (values) => {
    try {
      setSaving(true);

      await onSave({
        email: values.emailEnabled,
        sms: values.smsEnabled
      });

      SuccessMessage(t("redi.communication_settings.save_success"));
    } catch (error) {
      ErrorMessage(t("redi.communication_settings.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const hasPhone = patientPhone && patientPhone.trim() !== "";
  const hasEmail = patientEmail && patientEmail.trim() !== "";
  const hasMissingContactInfo = !hasPhone || !hasEmail;

  const formValues = form.getFieldsValue();
  const isValid = formValues.emailEnabled || formValues.smsEnabled;

  return (
    <Container>
      <BackButton id="comm-settings-back-btn" type="text" onClick={onClose}>
        <StyledBackIcon />
        <BackText>{t("back")}</BackText>
      </BackButton>

      <ContentSection>
        <Title id="comm-settings-title">{t("redi.communication_settings.title")}</Title>
        <Subtitle id="comm-settings-subtitle">{t("redi.communication_settings.subtitle")}</Subtitle>
      </ContentSection>

      <Form form={form} onFinish={handleFinish} onValuesChange={handleFormValuesChange} layout="vertical">
        <OptionsContainer>
          <OptionWrapper>
            <Form.Item name="smsEnabled" valuePropName="checked" noStyle>
              <StyledCheckbox
                id="comm-settings-sms-checkbox"
                disabled={!hasPhone}
                $hasError={!isValid && !hasMissingContactInfo}
              />
            </Form.Item>
            <OptionContent>
              <OptionLabel id="comm-settings-sms-label">{t("redi.communication_settings.sms_label")}</OptionLabel>
              <ContactInfo
                id="comm-settings-sms-contact-info"
                onClick={!hasPhone ? handleNavigateToContactDetails : undefined}
                style={!hasPhone ? { cursor: "pointer" } : undefined}
              >
                {hasPhone ? (
                  <>
                    <ContactText id="comm-settings-sms-phone-text">{formatPhoneNumber(patientPhone)}</ContactText>
                    <EditIcon id="comm-settings-sms-edit-icon" onClick={handleNavigateToContactDetails} />
                  </>
                ) : (
                  <>
                    <AddContactLink id="comm-settings-sms-add-link">
                      {t("redi.communication_settings.add_phone_number")}
                    </AddContactLink>
                    <EditIcon id="comm-settings-sms-add-icon" />
                  </>
                )}
              </ContactInfo>
            </OptionContent>
          </OptionWrapper>

          <OptionWrapper>
            <Form.Item name="emailEnabled" valuePropName="checked" noStyle>
              <StyledCheckbox
                id="comm-settings-email-checkbox"
                disabled={!hasEmail}
                $hasError={!isValid && !hasMissingContactInfo}
              />
            </Form.Item>
            <OptionContent>
              <OptionLabel id="comm-settings-email-label">{t("redi.communication_settings.email_label")}</OptionLabel>
              <ContactInfo
                id="comm-settings-email-contact-info"
                onClick={!hasEmail ? handleNavigateToContactDetails : undefined}
                style={!hasEmail ? { cursor: "pointer" } : undefined}
              >
                {hasEmail ? (
                  <>
                    <ContactText id="comm-settings-email-text">{patientEmail}</ContactText>
                    <EditIcon id="comm-settings-email-edit-icon" onClick={handleNavigateToContactDetails} />
                  </>
                ) : (
                  <>
                    <AddContactLink id="comm-settings-email-add-link">
                      {t("redi.communication_settings.add_email")}
                    </AddContactLink>
                    <EditIcon id="comm-settings-email-add-icon" />
                  </>
                )}
              </ContactInfo>
            </OptionContent>
          </OptionWrapper>
        </OptionsContainer>

        <ActionsSection id="comm-settings-actions-section" $hasError={hasMissingContactInfo || !isValid}>
          {hasMissingContactInfo && (
            <RediMissingContactBanner id="comm-settings-error-missing-details" onAdd={handleNavigateToContactDetails} />
          )}
          {formError && !hasMissingContactInfo && (
            <StyledErrorMessage id="comm-settings-error-no-selection">
              <ExclamationCircleOutlined id="comm-settings-error-no-selection-icon" />
              {formError}
            </StyledErrorMessage>
          )}

          <ButtonsGroup id="comm-settings-buttons-group">
            <RediSecondaryButton id="comm-settings-cancel-btn" type="text" onClick={handleCancel}>
              {t("redi.communication_settings.cancel")}
            </RediSecondaryButton>
            <RediPrimaryButton
              id="comm-settings-save-btn"
              type="primary"
              htmlType="submit"
              disabled={!isValid || saving || hasMissingContactInfo}
            >
              {saving ? t("redi.communication_settings.saving") : t("redi.communication_settings.save")}
            </RediPrimaryButton>
          </ButtonsGroup>
        </ActionsSection>
      </Form>
    </Container>
  );
};

export default CommunicationSettings;
